import {
  faCartShopping,
  faEnvelope,
  faGift,
  faMagnifyingGlass,
  faTriangleExclamation,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CrmSegment } from '../CrmSegment.enum';
import type { FetchSnapshot } from '../CrmUsers.page';
import * as styles from './CrmTableEmptyState.css';

const SEGMENT_COPY: Record<
  CrmSegment,
  { icon: IconProp; title: string; caption: string }
> = {
  [CrmSegment.Subscribed]: {
    icon: faUserGroup,
    title: 'No subscribers yet',
    caption: 'Subscribers will show up here once they join.',
  },
  [CrmSegment.Cancelled]: {
    icon: faUserGroup,
    title: 'No cancelled subscribers yet',
    caption: 'Cancelled subscribers will show up here.',
  },
  [CrmSegment.ExclusiveBuyers]: {
    icon: faCartShopping,
    title: 'No one-time purchases yet',
    caption: 'Buyers will show up here once they purchase.',
  },
  [CrmSegment.FreeOffers]: {
    icon: faGift,
    title: 'No free offer claims yet',
    caption: 'Claims will show up here once someone redeems an offer.',
  },
  [CrmSegment.Leads]: {
    icon: faEnvelope,
    title: 'No leads yet',
    caption: 'Leads will show up here once they come in.',
  },
  [CrmSegment.AbandonedCarts]: {
    icon: faCartShopping,
    title: 'No abandoned carts yet',
    caption: 'Abandoned checkouts will show up here.',
  },
};

type EmptyStateBodyProps = {
  testId: string;
  icon: IconProp;
  title: string;
  caption: string;
  failed?: boolean;
};

const EmptyStateBody = ({
  testId,
  icon,
  title,
  caption,
  failed,
}: EmptyStateBodyProps) => (
  <div data-testid={testId} className={styles.container}>
    <div
      className={`${styles.iconBadge} ${failed ? styles.iconBadgeFailed : ''}`}
    >
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className={styles.title}>{title}</div>
    <div className={styles.caption}>{caption}</div>
  </div>
);

type CrmTableEmptyStateProps = {
  segment: CrmSegment;
  snapshot: FetchSnapshot;
};

export const CrmTableEmptyState = ({
  segment,
  snapshot,
}: CrmTableEmptyStateProps) => {
  if (snapshot.status === 'idle' || snapshot.status === 'fetching') {
    return null;
  }

  if (snapshot.status === 'failed') {
    return (
      <EmptyStateBody
        testId="crm-empty-state-failed"
        icon={faTriangleExclamation}
        title="Couldn't load this list"
        caption="Something went wrong. Please try again."
        failed
      />
    );
  }

  if (snapshot.wasFiltered) {
    return (
      <EmptyStateBody
        testId="crm-empty-state-no-match"
        icon={faMagnifyingGlass}
        title="No results match these filters"
        caption="Try adjusting or clearing your filters or search."
      />
    );
  }

  return (
    <EmptyStateBody
      testId="crm-empty-state-no-data"
      {...SEGMENT_COPY[segment]}
    />
  );
};
